import React from 'react';
import { 
  Star, 
  CheckCircle2, 
  Lock, 
  Bot, 
  ChevronRight, 
  Clock, 
  Code, 
  FileText, 
  BrainCircuit, 
  Users 
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
const Interviewprep = () => {
    const navigate = useNavigate();
  const curriculumItems = [
    { 
      title: "Complete DSA Mastery", 
      desc: "From Arrays to Dynamic Programming. 100+ Solved Problems.",
      icon: <Code size={20} />
    },
    { 
      title: "MNC Specific Crack Sheet", 
      desc: "Exact questions asked in Google, Meta, Microsoft, Tesla.",
      icon: <FileText size={20} />
    },
    { 
      title: "Aptitude & Logical Reasoning", 
      desc: "Speed math, puzzles, and logic required for Round 1.",
      icon: <BrainCircuit size={20} />
    },
    { 
      title: "HR & Group Discussion", 
      desc: "How to speak, what to say, and behavioral interview tips.",
      icon: <Users size={20} />
    }
  ];

  return (
    <div className="min-h-screen bg-[#f8fafc] font-sans selection:bg-sky-100 pb-20">
      
      {/* --- SECTION 1: HERO (Image 1) --- */}
      <section className="pt-32 pb-16 px-4 text-center">
        <div className="max-w-5xl mx-auto flex flex-col items-center">
          {/* Premium Badge */}
          <div className="inline-flex items-center gap-2 bg-[#fff7ed] border border-[#ffedd5] px-4 py-1.5 rounded-full shadow-sm mb-8">
            <Star size={16} className="text-[#f97316] fill-[#f97316]" />
            <span className="text-[10px] font-black uppercase tracking-widest text-[#9a3412]">
              Premium Bundle
            </span>
          </div>

          <h1 className="text-5xl md:text-7xl font-serif text-[#0f172a] leading-tight mb-8">
            Master DSA & <span className="text-[#14b8a6]">Crack Placements</span>
          </h1>

          <p className="text-slate-500 text-lg md:text-xl font-medium max-w-3xl leading-relaxed mb-12">
            The ultimate all-in-one resource. Includes Data Structures, Algorithms, Aptitude, Company-Specific Questions (Google, Meta, Tesla), and HR/GD Preparation.
          </p>

          <div className="flex flex-wrap justify-center gap-4">
            <FeaturePill text="500+ DSA Problems" />
            <FeaturePill text="Top 50 MNC Sets" />
            <FeaturePill text="Lifetime Access" />
          </div>
        </div>
      </section>

      {/* --- SECTION 2: CONTENT GRID (Images 2 & 3) --- */}
      <main className="max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 mt-12">
        
        {/* Left: Curriculum (Locked Content) */}
        <div className="lg:col-span-8">
          <div className="flex justify-between items-center mb-8 border-b border-slate-100 pb-6">
            <h2 className="text-3xl font-serif text-[#0f172a]">Course Curriculum</h2>
            <div className="flex items-center gap-2 text-slate-400">
              <Lock size={14} />
              <span className="text-[10px] font-bold tracking-widest uppercase">Locked Content</span>
            </div>
          </div>

          <div className="space-y-5">
            {curriculumItems.map((item, idx) => (
              <div key={idx} className="relative group overflow-hidden rounded-2xl">
                {/* Blurred Content Card */}
                <div className="bg-white p-7 border border-slate-100 flex items-center gap-6 blur-[1.5px] opacity-50 grayscale-[0.5]">
                  <div className="w-14 h-14 bg-slate-50 rounded-2xl flex items-center justify-center text-slate-400 shadow-inner">
                    {item.icon}
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-800 text-xl mb-1">{item.title}</h3>
                    <p className="text-slate-500 font-medium">{item.desc}</p>
                  </div>
                </div>
                
                {/* Lock Icon Overlay */}
                <div className="absolute inset-0 flex items-center justify-center bg-white/10">
                  <div className="w-12 h-12 bg-white border border-slate-200 rounded-full shadow-lg flex items-center justify-center text-slate-400 group-hover:scale-110 transition-transform duration-300">
                    <Lock size={20} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right: Sticky Sidebar Cards */}
        <div className="lg:col-span-4 space-y-8">
          
          {/* AI Resume Scanner Card */}
          <div className="bg-gradient-to-br from-[#6366f1] to-[#a855f7] rounded-[2.5rem] p-8 text-white shadow-2xl shadow-indigo-500/30 relative overflow-hidden group">
            <div className="relative z-10">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center mb-6 border border-white/30">
                <Bot size={24} />
              </div>
              <h3 className="text-3xl font-serif italic mb-4">AI Resume Scanner</h3>
              <p className="text-indigo-50/90 font-medium text-sm leading-relaxed mb-10">
                Check your ATS score and get instant feedback for MNC roles (Google, Microsoft) using AI.
              </p>
              <button onClick={()=>navigate("/resume-scanner")} className="w-full bg-white text-[#6366f1] font-black py-4 rounded-2xl flex items-center justify-center gap-2 hover:bg-indigo-50 transition-all shadow-xl active:scale-95">
                Try It Free <ChevronRight size={20} />
              </button>
            </div>
            <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-3xl group-hover:bg-white/20 transition-all duration-500"></div>
          </div>

          {/* Pricing Card (Updated from Image 3) */}
          <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-[0_20px_50px_rgba(0,0,0,0.04)] overflow-hidden relative">
            {/* Discount Badge */}
            <div className="absolute top-0 right-0">
              <div className="bg-[#f43f5e] text-white text-[10px] font-black px-5 py-2 rounded-bl-3xl shadow-sm uppercase tracking-tighter">
                80% OFF
              </div>
            </div>

            <div className="p-10 pt-14">
              <p className="text-[11px] font-bold text-slate-400 tracking-[0.2em] uppercase mb-4">
                Total Bundle Price
              </p>
              <div className="flex items-baseline gap-4 mb-10">
                <span className="text-6xl font-bold text-[#0f172a]">₹499</span>
                <span className="text-2xl text-slate-300 line-through font-medium">₹2499</span>
              </div>

              <div className="space-y-5 mb-12">
                <PriceFeature text="Complete DSA Roadmap" />
                <PriceFeature text="Aptitude & Reasoning Logic" />
                <PriceFeature text="Company Specific Crack Sheet" />
                <PriceFeature text="HR & GD Prep Guide" />
              </div>

              {/* Updated Button from Image 3 */}
              <button className="w-full bg-[#0a1120] hover:bg-black text-white font-black py-5 rounded-2xl flex items-center justify-center gap-3 transition-all shadow-2xl shadow-slate-900/20 active:scale-95 mb-6">
                Unlock Bundle Now
                <ChevronRight size={22} strokeWidth={3} />
              </button>

              {/* Guarantee Footer */}
              <div className="flex items-center justify-center gap-2 text-[#94a3b8] text-[12px] font-bold">
                <Clock size={15} />
                <span>100% Money Back Guarantee (7 Days)</span>
              </div>
            </div>
          </div>

        </div>
      </main>
    </div>
  );
};

// Internal Components
const FeaturePill = ({ text }) => (
  <div className="flex items-center gap-2 bg-white px-6 py-3.5 rounded-full border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.02)] hover:shadow-md transition-all duration-300">
    <CheckCircle2 size={18} className="text-[#22c55e]" />
    <span className="text-sm font-black text-slate-600">{text}</span>
  </div>
);

const PriceFeature = ({ text }) => (
  <div className="flex items-center gap-4">
    <div className="bg-emerald-50 p-0.5 rounded-full">
      <CheckCircle2 size={20} className="text-[#22c55e]" />
    </div>
    <span className="text-sm font-bold text-slate-600">{text}</span>
  </div>
);

export default Interviewprep;